import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Add, ArrowBack, PlaylistAdd } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetAnalysisNoteQuery } from '../../api/analysis-notes'
import {
  useGetInternalBulkAssignGamesQuery,
  useGetInternalFeatureGameChoicesQuery,
  useSaveInternalFeatureBulkAssignMutation,
} from '../../api/feature-bulk-assign'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetInternalFeatureMapAllMarketsQuery } from '../../api/screenshot-links'
import { AnalysisNoteEditDialog } from '../../components/AnalysisNoteEditDialog/AnalysisNoteEditDialog'
import { AnalysisNoteViewDialog } from '../../components/AnalysisNoteViewDialog/AnalysisNoteViewDialog'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { BenchmarkFeatureDialog } from '../../features/categories/components/BenchmarkFeatureDialog/BenchmarkFeatureDialog'
import { BulkAssignGamesTable } from '../../features/categories/components/BulkAssignGamesTable/BulkAssignGamesTable'
import { FeatureScreenshotsDialog } from '../../features/categories/components/FeatureScreenshotsDialog/FeatureScreenshotsDialog'
import {
  addAllIsos,
  addIso,
  applyBulkChoice,
  countWithoutChoice,
  filterBulkAssignGames,
  getBulkAssignIsos,
  getFeatureMarkets,
  getGameIdsWithoutChoice,
  getUnstoredAnswers,
  maxBenchmarkFeatures,
  selectGameChoice,
  toBenchmarkFeature,
  toBulkAssignBody,
  toInitialState,
} from '../../features/categories/components/helpers/bulkAssignHelpers'
import { BulkAssignFeature, BulkAssignGame, BulkAssignState, GameChoiceMap } from '../../features/categories/types/BulkAssign'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

const listPath = '/internal/categories'
const maxChoiceButtons = 2
// Unstored games named in the message; the rest are counted.
const maxNamedGames = 5

const discardAction = 'discard'

type MarketSelection = { addedIsos: string[]; selectedIso?: string }
type Loaded = { key: string; stored: GameChoiceMap; state: BulkAssignState; version: number }
type GameFeatureTarget = { game: BulkAssignGame; feature: BulkAssignFeature }

const ChoicePicker: FC<{
  feature: BulkAssignFeature
  value?: number
  disabled: boolean
  label: string
  onChange: (weightId: number) => void
}> = ({ feature, value, disabled, label, onChange }) => {
  const choices = feature.choices ?? []
  if (choices.length <= maxChoiceButtons) {
    return (
      <ButtonGroup disabled={disabled} aria-label={label}>
        {choices.map((choice) => (
          <Button
            key={choice.weightId}
            variant={value === choice.weightId ? 'contained' : 'outlined'}
            aria-pressed={value === choice.weightId}
            onClick={() => onChange(choice.weightId)}
          >
            {choice.choice}
          </Button>
        ))}
      </ButtonGroup>
    )
  }
  return (
    <FormControl sx={{ minWidth: 200 }} size="small">
      <InputLabel id="bulk-choice">{label}</InputLabel>
      <Select labelId="bulk-choice" label={label} value={value ?? ''} disabled={disabled} onChange={(event) => onChange(Number(event.target.value))}>
        {choices.map((choice) => (
          <MenuItem key={choice.weightId} value={choice.weightId}>
            {choice.choice}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export const FeatureBulkAssignPage: FC = () => {
  const { featureLegacyId = '' } = useParams<{ featureLegacyId: string }>()
  usePage(pageService.getInternalPageWithId('internal-feature-bulk-assign'))
  const { t } = useTranslation()
  const { run, notifyError, notifySuccess } = useMutationFeedback()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const legacyId = Number(featureLegacyId)

  const featureMapQuery = useGetInternalFeatureMapAllMarketsQuery()
  const marketsQuery = useGetInternalMarketsQuery()
  const [saveBulkAssign, { isLoading: isSaving }] = useSaveInternalFeatureBulkAssignMutation()

  const feature = useMemo(() => {
    const found = Object.values(featureMapQuery.data ?? {}).find((candidate) => candidate.legacyId === legacyId)
    return found ? toBenchmarkFeature(found) : undefined
  }, [featureMapQuery.data, legacyId])
  const featureMarkets = useMemo(() => getFeatureMarkets(feature, marketsQuery.data), [feature, marketsQuery.data])
  const marketNames = useMemo(() => new Map(featureMarkets.map((market) => [market.iso, market.name])), [featureMarkets])
  const requestedIso = searchParams.get('marketIso') ?? undefined
  const selectedIso = featureMarkets.find((market) => market.iso === requestedIso)?.iso ?? featureMarkets[0]?.iso

  const [addedIsos, setAddedIsos] = useState<string[]>([])
  const isos = useMemo(() => getBulkAssignIsos(addedIsos, selectedIso), [addedIsos, selectedIso])
  const isosKey = isos.join(',')
  const skipData = !feature || !isos.length
  const choicesQuery = useGetInternalFeatureGameChoicesQuery({ legacyId, isos }, { skip: skipData })
  const gamesQuery = useGetInternalBulkAssignGamesQuery({ isos }, { skip: skipData })

  const [benchmarks, setBenchmarks] = useState<BulkAssignFeature[]>([])
  const benchmarkQuery0 = useGetInternalFeatureGameChoicesQuery({ legacyId: benchmarks[0]?.legacyId ?? 0, isos }, { skip: skipData || !benchmarks[0] })
  const benchmarkQuery1 = useGetInternalFeatureGameChoicesQuery({ legacyId: benchmarks[1]?.legacyId ?? 0, isos }, { skip: skipData || !benchmarks[1] })
  const benchmarkAnswers = useMemo(() => {
    const answers: Record<number, GameChoiceMap | undefined> = {}
    if (benchmarks[0]) answers[benchmarks[0].legacyId] = benchmarkQuery0.currentData
    if (benchmarks[1]) answers[benchmarks[1].legacyId] = benchmarkQuery1.currentData
    return answers
  }, [benchmarks, benchmarkQuery0.currentData, benchmarkQuery1.currentData])

  const [loaded, setLoaded] = useState<Loaded>()
  const [bulkWeightId, setBulkWeightId] = useState<number>()
  const [force, setForce] = useState(false)
  const [search, setSearch] = useState('')
  const [onlyWithout, setOnlyWithout] = useState(false)
  const [onlyIds, setOnlyIds] = useState<Set<string>>()
  const [pendingSelection, setPendingSelection] = useState<MarketSelection>()
  const [benchmarkDialogOpen, setBenchmarkDialogOpen] = useState(false)
  const [noteTarget, setNoteTarget] = useState<GameFeatureTarget>()
  const [isEditingNote, setIsEditingNote] = useState(false)
  const [screenshotsTarget, setScreenshotsTarget] = useState<GameFeatureTarget>()

  const noteQuery = useGetAnalysisNoteQuery(
    { gameId: noteTarget?.game.id ?? '', featureLegacyId: noteTarget?.feature.legacyId ?? 0 },
    { skip: !noteTarget, refetchOnMountOrArgChange: true }
  )
  const note = noteTarget ? noteQuery.currentData : undefined

  const games = gamesQuery.currentData
  const storedChoices = choicesQuery.currentData
  const isLoaded = loaded?.key === isosKey && !!games
  const state = isLoaded ? loaded.state : undefined
  const changedIds = useMemo(() => new Set([...Object.keys(state?.changedBulk ?? {}), ...Object.keys(state?.changed ?? {})]), [state])
  const body = useMemo(() => (state ? toBulkAssignBody(state) : {}), [state])
  const changeCount = Object.keys(body).length
  const hasChanges = changedIds.size > 0
  const { guard } = useEditPageLeave(hasChanges)

  // Answers are (re)loaded only for a new market list, so a refetch can't replace changes that weren't stored.
  useEffect(() => {
    if (storedChoices && !choicesQuery.isFetching && loaded?.key !== isosKey) {
      setLoaded((current) => ({ key: isosKey, stored: storedChoices, state: toInitialState(storedChoices), version: (current?.version ?? 0) + 1 }))
    }
  }, [storedChoices, choicesQuery.isFetching, loaded?.key, isosKey])

  // Legacy's snapshot: retaken when the answers reload, not when they change.
  const onlyWithoutRef = useRef(onlyWithout)
  onlyWithoutRef.current = onlyWithout
  const loadedVersion = isLoaded ? loaded.version : undefined
  const loadedStored = isLoaded ? loaded.stored : undefined
  useEffect(() => {
    if (onlyWithoutRef.current && games && loadedStored) setOnlyIds(getGameIdsWithoutChoice(games, loadedStored))
  }, [loadedVersion, loadedStored, games])

  const updateState = (update: (current: BulkAssignState, stored: GameChoiceMap) => BulkAssignState) =>
    setLoaded((current) => (current ? { ...current, state: update(current.state, current.stored) } : current))

  const applySelection = (selection: MarketSelection) => {
    setAddedIsos(selection.addedIsos)
    if (selection.selectedIso && selection.selectedIso !== selectedIso) {
      const next = new URLSearchParams(searchParams)
      next.set('marketIso', selection.selectedIso)
      setSearchParams(next, { replace: true })
    }
  }

  const requestSelection = (selection: MarketSelection) => {
    const nextKey = getBulkAssignIsos(selection.addedIsos, selection.selectedIso).join(',')
    if (hasChanges && nextKey !== isosKey) setPendingSelection(selection)
    else applySelection(selection)
  }

  const listedGames = useMemo(() => (games ?? []).filter((game) => !onlyIds || onlyIds.has(game.id)), [games, onlyIds])
  const visibleGames = useMemo(() => filterBulkAssignGames(listedGames, search), [listedGames, search])
  const applyTargets = state ? listedGames.filter((game) => force || !state.answers[game.id]).length : 0
  const withoutChoice = countWithoutChoice(games, state?.answers ?? {})
  const marketLabel = (iso: string) => marketNames.get(iso) ?? iso

  const handleOnlyWithout = (checked: boolean) => {
    setOnlyWithout(checked)
    setOnlyIds(checked && games && state ? getGameIdsWithoutChoice(games, state.answers) : undefined)
  }

  const handleApply = () => {
    if (bulkWeightId === undefined) return
    updateState((current) => applyBulkChoice(current, listedGames, bulkWeightId, force))
  }

  const gameName = (gameId: string) => games?.find((game) => game.id === gameId)?.name ?? gameId

  const handleSave = async () => {
    if (!changeCount) return
    const sentIsos = isos
    const saved = await run(saveBulkAssign({ legacyId, isos: sentIsos, answers: body }).unwrap(), {
      error: t('internal-analysts:bulk_assign_save_failed'),
      errorWithReason: (reason) => t('internal-analysts:bulk_assign_save_failed_reason', { reason }),
    })
    if (!saved.ok) return
    const { storedByIso } = saved.result
    const unstored = getUnstoredAnswers(body, storedByIso)
    if (unstored.length) {
      const named = unstored
        .slice(0, maxNamedGames)
        .map((answer) =>
          t('internal-analysts:bulk_assign_not_stored_game', { game: gameName(answer.gameId), markets: answer.isos.map(marketLabel).join(', ') })
        )
        .join('; ')
      const more = unstored.length > maxNamedGames ? ` ${t('internal-analysts:bulk_assign_not_stored_more', { count: unstored.length - maxNamedGames })}` : ''
      notifyError(t('internal-analysts:bulk_assign_not_stored', { count: unstored.length, total: changeCount, games: named }) + more)
      return
    }
    const stored = Object.assign({}, ...sentIsos.map((iso) => storedByIso[iso])) as GameChoiceMap
    setLoaded((current) => ({ key: sentIsos.join(','), stored, state: toInitialState(stored), version: (current?.version ?? 0) + 1 }))
    notifySuccess(t('internal-analysts:bulk_assign_saved', { count: changeCount, markets: sentIsos.map(marketLabel).join(', ') }))
  }

  const isLoadingFeature = featureMapQuery.isLoading || marketsQuery.isLoading
  const featureError = featureMapQuery.isError
    ? t('internal-analysts:bulk_assign_feature_load_failed')
    : featureMapQuery.data && !feature
    ? t('internal-analysts:bulk_assign_feature_not_found', { id: featureLegacyId })
    : feature && marketsQuery.data && !featureMarkets.length
    ? t('internal-analysts:bulk_assign_no_markets')
    : undefined
  const dataError = choicesQuery.isError || gamesQuery.isError ? t('internal-analysts:bulk_assign_load_failed') : undefined
  const busy = isSaving || !isLoaded

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)}>
            {t('internal-common:back')}
          </Button>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {t('internal-analysts:bulk_assign_title')}
            </Typography>
            {feature && (
              <Typography variant="body2" color="text.secondary">
                {t('internal-analysts:bulk_assign_feature', { name: feature.name, id: feature.legacyId })}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          {hasChanges && (
            <Typography variant="body2" color="warning.main">
              {t('internal-analysts:bulk_assign_change_count', { count: changeCount })}
            </Typography>
          )}
          <Button variant="contained" onClick={handleSave} disabled={busy || !changeCount}>
            {t('internal-common:save')}
          </Button>
        </Stack>
      </Stack>

      {isLoadingFeature ? (
        <Skeleton variant="rectangular" height={480} />
      ) : featureError || !feature ? (
        <Typography color="error.main">{featureError}</Typography>
      ) : (
        <Stack spacing={2}>
          <Alert severity="warning">{t('internal-analysts:bulk_assign_customer_note')}</Alert>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <FormControl sx={{ minWidth: 240 }} size="small">
                    <InputLabel id="bulk-market">{t('internal-analysts:bulk_assign_market')}</InputLabel>
                    <Select
                      labelId="bulk-market"
                      label={t('internal-analysts:bulk_assign_market')}
                      value={selectedIso ?? ''}
                      disabled={isSaving}
                      onChange={(event) => requestSelection({ addedIsos, selectedIso: event.target.value })}
                    >
                      {featureMarkets.map((market) => (
                        <MenuItem key={market.iso} value={market.iso}>
                          {market.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    startIcon={<Add />}
                    disabled={isSaving || !selectedIso || addedIsos.includes(selectedIso)}
                    onClick={() => selectedIso && requestSelection({ addedIsos: addIso(addedIsos, selectedIso), selectedIso })}
                  >
                    {t('internal-analysts:bulk_assign_add_market')}
                  </Button>
                  <Button
                    startIcon={<PlaylistAdd />}
                    disabled={isSaving || addedIsos.length === featureMarkets.length}
                    onClick={() => requestSelection({ addedIsos: addAllIsos(addedIsos, featureMarkets), selectedIso })}
                  >
                    {t('internal-analysts:bulk_assign_add_all_markets')}
                  </Button>
                </Stack>
                {addedIsos.length > 0 && (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {addedIsos.map((iso) => (
                      <Chip
                        key={iso}
                        label={marketLabel(iso)}
                        disabled={isSaving}
                        onDelete={() => requestSelection({ addedIsos: addedIsos.filter((added) => added !== iso), selectedIso })}
                      />
                    ))}
                  </Stack>
                )}
                <Typography variant="body2" color="text.secondary">
                  {t('internal-analysts:bulk_assign_markets_used', { markets: isos.map(marketLabel).join(', ') })}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">{t('internal-analysts:bulk_assign_set_for_all')}</Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <ChoicePicker
                    feature={feature}
                    value={bulkWeightId}
                    disabled={busy}
                    label={t('internal-analysts:bulk_assign_bulk_choice')}
                    onChange={setBulkWeightId}
                  />
                  <Button variant="outlined" onClick={handleApply} disabled={busy || bulkWeightId === undefined}>
                    {t('internal-analysts:bulk_assign_apply', { count: applyTargets })}
                  </Button>
                  <FormControlLabel
                    control={<Checkbox checked={force} onChange={(event) => setForce(event.target.checked)} disabled={busy} />}
                    label={t('internal-analysts:bulk_assign_force')}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {force ? t('internal-analysts:bulk_assign_apply_force_help') : t('internal-analysts:bulk_assign_apply_help')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label={t('internal-analysts:bulk_assign_search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: 240 }}
            />
            <FormControlLabel
              control={<Checkbox checked={onlyWithout} onChange={(event) => handleOnlyWithout(event.target.checked)} disabled={!isLoaded} />}
              label={t('internal-analysts:bulk_assign_only_without')}
            />
            <Button variant="outlined" onClick={() => setBenchmarkDialogOpen(true)} disabled={!selectedIso}>
              {t('internal-analysts:bulk_assign_benchmark_button', { count: benchmarks.length, max: maxBenchmarkFeatures })}
            </Button>
            {benchmarks.map((benchmark) => (
              <Chip
                key={benchmark.legacyId}
                label={benchmark.name}
                onDelete={() => setBenchmarks((current) => current.filter((candidate) => candidate.legacyId !== benchmark.legacyId))}
              />
            ))}
          </Stack>
          {isLoaded && (
            <Stack direction="row" spacing={2} alignItems="baseline">
              <Typography variant="body2" fontWeight="bold" color={withoutChoice > 0 ? 'error.main' : 'success.main'}>
                {t('internal-analysts:bulk_assign_without_choice', { count: withoutChoice })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('internal-analysts:bulk_assign_showing', { count: visibleGames.length })}
              </Typography>
            </Stack>
          )}

          {dataError ? (
            <Typography color="error.main">{dataError}</Typography>
          ) : !isLoaded || !state ? (
            <Skeleton variant="rectangular" height={480} />
          ) : (
            <BulkAssignGamesTable
              games={visibleGames}
              feature={feature}
              answers={state.answers}
              changedIds={changedIds}
              marketIso={selectedIso}
              benchmarks={benchmarks}
              benchmarkAnswers={benchmarkAnswers}
              disabled={isSaving}
              onSelectChoice={(gameId, weightId) => updateState((current, stored) => selectGameChoice(current, stored, gameId, weightId))}
              onShowNotes={(game, target) => setNoteTarget({ game, feature: target })}
              onShowScreenshots={(game, target) => setScreenshotsTarget({ game, feature: target })}
            />
          )}
        </Stack>
      )}

      <BenchmarkFeatureDialog
        open={benchmarkDialogOpen}
        marketIso={selectedIso ?? ''}
        excludedLegacyId={legacyId}
        selected={benchmarks}
        onClose={() => setBenchmarkDialogOpen(false)}
        onDone={(picked) => {
          setBenchmarks(picked)
          setBenchmarkDialogOpen(false)
        }}
      />
      <AnalysisNoteViewDialog
        open={!!noteTarget && !isEditingNote}
        title={t('internal-analysts:analysis_notes_view_title', { name: noteTarget?.feature.name ?? '' })}
        subtitle={noteTarget?.game.name}
        notes={note?.notes}
        isLoading={noteQuery.isFetching}
        isError={noteQuery.isError}
        emptyText={t('internal-analysts:analysis_notes_none')}
        onClose={() => setNoteTarget(undefined)}
        onEdit={note ? () => setIsEditingNote(true) : undefined}
      />
      {/* Legacy's bulk assign only edits a stored note: no add, no delete. */}
      <AnalysisNoteEditDialog
        open={!!noteTarget && isEditingNote && !!note}
        note={note ?? undefined}
        featureName={noteTarget?.feature.name}
        gameName={noteTarget?.game.name}
        canDelete={false}
        onClose={() => setIsEditingNote(false)}
      />
      <FeatureScreenshotsDialog target={screenshotsTarget} onClose={() => setScreenshotsTarget(undefined)} />
      <ConfirmDialog
        open={!!pendingSelection}
        confirmDialogData={{
          title: t('internal-analysts:bulk_assign_discard_title'),
          confirmText: t('internal-analysts:bulk_assign_discard_markets_confirm'),
          actionText: t('internal-analysts:bulk_assign_discard'),
          destructiveAction: true,
          data: discardAction,
        }}
        onClose={(confirmed) => {
          if (confirmed?.data === discardAction && pendingSelection) applySelection(pendingSelection)
          setPendingSelection(undefined)
        }}
      />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:bulk_assign_discard_title')}
        text={t('internal-analysts:bulk_assign_discard_leave_confirm')}
        discardText={t('internal-analysts:bulk_assign_discard')}
      />
    </Box>
  )
}
